import { QueryClient, type InfiniteData } from "@tanstack/react-query";
import type { Post } from "@atrium/shared";
import { ApiError } from "./api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
    mutations: { retry: false },
  },
});

export type Infinite<T> = InfiniteData<T, unknown>;

/** Recursively apply `fn` to every Post with the given id inside arbitrary cached data. */
function deepMapPosts(data: unknown, postId: string, fn: (p: Post) => Post): unknown {
  if (Array.isArray(data)) {
    let changed = false;
    const out = data.map((d) => {
      const n = deepMapPosts(d, postId, fn);
      if (n !== d) changed = true;
      return n;
    });
    return changed ? out : data;
  }
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (obj.id === postId && "likeCount" in obj && "author" in obj) return fn(obj as unknown as Post);
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      const n = deepMapPosts(v, postId, fn);
      if (n !== v) changed = true;
      out[k] = n;
    }
    return changed ? out : data;
  }
  return data;
}

const POST_KEYS = new Set(["feed", "post", "profile-posts", "community-posts", "topic-posts", "discover", "search"]);

export function updatePostEverywhere(postId: string, fn: (p: Post) => Post) {
  queryClient.setQueriesData({ predicate: (q) => POST_KEYS.has(String(q.queryKey[0])) }, (old: unknown) =>
    old === undefined ? old : deepMapPosts(old, postId, fn),
  );
}

/** Remove a post (and reposts of it) from cached lists after deletion. */
export function removePostEverywhere(postId: string) {
  const strip = (data: unknown): unknown => {
    if (Array.isArray(data)) {
      return data
        .filter((d) => {
          if (!d || typeof d !== "object") return true;
          const o = d as Record<string, unknown>;
          if (o.id === postId && "likeCount" in o) return false;
          if ("post" in o && (o.post as { id?: string })?.id === postId) return false;
          return true;
        })
        .map(strip);
    }
    if (data && typeof data === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(data as Record<string, unknown>)) out[k] = strip(v);
      return out;
    }
    return data;
  };
  queryClient.setQueriesData({ predicate: (q) => POST_KEYS.has(String(q.queryKey[0])) && q.queryKey[0] !== "post" }, (old: unknown) =>
    old === undefined ? old : strip(old),
  );
}
